import "katex/dist/katex.min.css";

import type { FC } from "react";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import Latex from "react-latex-next";

import { TutoringFooter } from "./components/TutoringFooter";
import { TutoringHeader } from "./components/TutoringHeader";

import { ErrorModal } from "components";
import AnswerTutoring from "components/Answer/AnswerTutoring";
import { FirstIncorrectAnswerModal } from "components/Modal/IncorrectAnswer/FirstIncorrectAnswer";
import { SecondIncorrectAnswerModal } from "components/Modal/IncorrectAnswer/SecondIncorrectAnswer";
import { TutoringSelfEvaluation } from "features/SelfEvaluation/Tutoring/_SelfEvaluation";
import { BooleanWithTypeGuard } from "utils/types/BooleanWithTypeGuard";

import type { CheckTutoringAnswerMutation, UserAssignmentForExerciseFragment } from "api/generated";
import { useCheckTutoringAnswerMutation } from "api/generated";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { getAssignmentTime, resetAssignmentTimer, startAssignmentTimer, stopAssignmentTimer } from "store/slices/timer";

import { getTimeToLearn } from "utils/getTimeToLearn";
import { processExerciseError } from "utils/processApiError";
import { useTutoringContext } from "./context/TutoringContext";

interface Props {
  data: UserAssignmentForExerciseFragment;
  userAssignmentId: string;
  onNextClick: () => void;
}

const JednoslovneOdpovedi: FC<Props> = ({ data, userAssignmentId, onNextClick }) => {
  const isSelfEvaluation = data.assignment?.scoring?.scoringMethod.scoringMethod === "sebeopravení";
  const questionsCount = data.assignment?.assignment?.questions.length ?? 1;

  const dispatch = useAppDispatch();
  const [answers, setAnswers] = useState<string[]>(new Array(questionsCount).fill(""));
  const [apiResults, setApiResults] = useState<boolean[]>([]);
  const time = useAppSelector(getAssignmentTime);

  const [sendAnswers, { error: apiMutationError, isLoading: isSendingAnswers }] = useCheckTutoringAnswerMutation();

  const [submit, setSubmit] = useState(false);
  const [isErrorModalVisible, setErrorModalVisible] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const { videoTips, tutoringId, claimEvaluation } = useTutoringContext();
  const [activeAccordion, setAccordionActive] = useState<string | string[] | undefined | null>(null);

  useEffect(() => {
    dispatch(resetAssignmentTimer());
    dispatch(startAssignmentTimer());

    return () => {
      dispatch(resetAssignmentTimer());
    };
  }, []);

  useEffect(() => {
    if (apiMutationError === undefined) return;

    processExerciseError(apiMutationError, {
      assignmentId: userAssignmentId,
      selfEvaluation: null,
      userAnswers: answers ?? [],
      timeToSolve: time,
    });
    setErrorModalVisible(true);
  }, [apiMutationError]);

  useEffect(() => {
    if (!submit) return;

    sendAnswers({
      tutoringId,
      userAssignmentId,
      selfEvaluation: null,
      userAnswers: answers.map(answer => ({
        answers: [answer.trim()],
      })),
      timeToSolve: time,
    })
      .then(response => {
        if ("data" in response && response.data) {
          setApiResults(response.data.tutoringAnswer.questionResults ?? []);
          processAnswerResponse(response);
          setErrorModalVisible(false);

          setAccordionActive(prev => {
            if (prev) return prev;
            const results = response.data.tutoringAnswer.questionResults ?? [];
            return results.length > 0 && !results.every(result => result) ? "0" : null;
          });
        }
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        dispatch(resetAssignmentTimer());
        dispatch(startAssignmentTimer());
        setSubmit(false);
      });
  }, [submit]);

  const processAnswerResponse = (response: { data: CheckTutoringAnswerMutation }) => {
    const { questionResults } = response.data.tutoringAnswer;

    if (!questionResults) return;

    const answersCopy = [...(answers ?? [])];
    questionResults.forEach((result, index) => {
      if (!result) answersCopy[index] = "";
    });

    setAnswers(answersCopy);
  };

  const onSubmit = () => {
    setSubmit(true);
    setAttempt(prev => prev + 1);
  };

  const handleNextClick = () => {
    dispatch(stopAssignmentTimer());
    onNextClick();
  };

  const handleChange = (index: number, value: string) => {
    const copy = [...answers];
    copy[index] = value;
    setAnswers(copy);
  };

  useEffect(() => {
    const areAllCorrect = apiResults.length > 0 && apiResults.every(result => result);
    const userAnswers = isSelfEvaluation ? new Array(questionsCount).fill(true) : apiResults;

    if (areAllCorrect) {
      claimEvaluation({ tutoringId, userAssignmentId, userAnswers }).catch(() => {});
    }
  }, [answers]);

  const areAnswersFilled = () => answers.every(answer => typeof answer === "string" && answer.trim().length > 0);

  const toggleAccordion = (state: boolean) => {
    setAccordionActive(state ? "0" : null);
  };

  const videoIds =
    data.assignment?.analysis?.hasSharedAnalysis === true
      ? [data.assignment.analysis.sharedAnalysis?.explainerVideo].filter(BooleanWithTypeGuard)
      : [
          ...new Set(
            data.assignment?.assignment?.questions
              .map(question => question.solutionAnalysis?.explainerVideo)
              .filter(BooleanWithTypeGuard),
          ),
        ];

  if (isSelfEvaluation) {
    return (
      <>
        <TutoringSelfEvaluation
          userAssignment={data}
          userAssignmentId={data.id}
          onNextClick={handleNextClick}
          videoTips={videoTips}
        />
      </>
    );
  }

  return (
    <div className="exercise__body">
      <TutoringHeader
        data={data}
        userAssignmentId={userAssignmentId}
        activeAccordion={activeAccordion}
        toggleAccordion={toggleAccordion}
        videoIds={videoIds}
        videoTips={videoTips}
      />

      <div>
        {data.assignment?.assignment?.questions.map((question, qIndex) => (
          <div
            key={question.heading}
            className={`question ${apiResults.at(qIndex) === false ? "question--incorrect" : ""}`}
            {...(qIndex === 0 ? { id: "question" } : {})}
          >
            <Latex>{question.heading ?? ""}</Latex>

            {apiResults[qIndex] ? (
              <AnswerTutoring
                subject={data.subject?.appName}
                key={`answer-${qIndex}`}
                index={qIndex}
                assignment={data.assignment}
                userAssignmentId={data.id}
                type={apiResults?.[qIndex] ? "success" : "danger"}
                timeToLearn={getTimeToLearn(data.timeToLearn)}
              >
                {answers[qIndex]}
              </AnswerTutoring>
            ) : (
              <Form.Control
                onChange={event => {
                  handleChange(qIndex, event.target.value);
                }}
                value={answers[qIndex]}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
            )}
          </div>
        ))}

        <ErrorModal
          onCancel={() => {
            setErrorModalVisible(false);
          }}
          onHide={() => {
            setErrorModalVisible(false);
          }}
          onRetry={() => {
            onSubmit();
          }}
          show={isErrorModalVisible}
        />

        {!isSendingAnswers && apiResults.includes(false) && (
          <>
            {!videoIds?.length || attempt === 1 ? (
              <FirstIncorrectAnswerModal
                incorrectCount={apiResults.filter(result => !result).length}
                questionsCount={questionsCount}
              />
            ) : null}

            {videoIds?.length && attempt > 1 ? (
              <SecondIncorrectAnswerModal videoIds={videoIds} attempt={attempt} />
            ) : null}
          </>
        )}

        <TutoringFooter
          onSubmit={onSubmit}
          isAnalysis={apiResults.length > 0 && apiResults.every(result => result)}
          goNext={handleNextClick}
          isSubmitDisabled={!areAnswersFilled() || isSendingAnswers}
        />
      </div>
    </div>
  );
};

export default JednoslovneOdpovedi;
